import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GrantsAndAllowedApis,
  Provider,
  QueryAndOutputs,
  TokenAllowedApis,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentGetRequiredGrantsParams,
  WorkflowComponentParamType
} from '../types'
import { SINGLE_OUTPUT_NAME, SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class ImportURLFromTable extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.importurlusingtable',
        group: 'Input/Output',
        title: 'Import from URL using table',
        description: `
      This components imports a table from a URL contained in a table.
      `,
        starred: true,
        warningTitle: `Run required`,
        warningDescription: `It's required to run the workflow before connecting it to any other element. This process may take several minutes.`
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'urlColumn',
          type: WorkflowComponentParamType.Column,
          parent: 'source',
          dataType: [ColumnDataType.String],
          title: 'URL column',
          description: 'Column containing the URL to import'
        },
        {
          name: 'autoguessing',
          type: WorkflowComponentParamType.Boolean,
          title: 'Automatically guess column data types in the imported table',
          default: true
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    let query
    const dryRun = codeType === WorkflowCodeType.DryRun
    const fullRunParams = workflow.resolveParameterValues({
      connection,
      codeType: WorkflowCodeType.Full
    })
    const tablenameFull = connection.getOutputTableName(
      workflow.id,
      componentName,
      fullRunParams[componentName],
      SINGLE_OUTPUT_NAME,
      WorkflowCodeType.Full
    )
    const tablenameDryRun = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      WorkflowCodeType.DryRun
    )
    if (dryRun) {
      query = connection.getCreateTempTableQuery(
        tablenameDryRun,
        `SELECT * FROM ${connection.quoteName(tablenameFull)}`,
        codeType,
        null
      )
    } else {
      query = this.getInnerSql({
        params,
        connection,
        envVars,
        tablename: tablenameFull,
        workflow,
        componentName
      } as WorkflowComponentCodeParams)
    }
    const outputs: Record<string, string> = {}
    outputs[SINGLE_OUTPUT_NAME] = dryRun ? tablenameDryRun : tablenameFull
    return {
      outputs: outputs,
      query: query
    }
  }

  getInnerSql({ params, connection, envVars, tablename }: WorkflowComponentCodeParams) {
    const preparedParams = this._prepareParameters(params)

    const urlTable = connection.quoteName(params.source as string)
    const urlColumn = params.urlColumn as string

    switch (connection.type) {
      case Provider.BigQuery:
        return dedent`
            DECLARE url STRING;
            SET url = (SELECT ${urlColumn} FROM ${urlTable} LIMIT 1);
            CALL ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.IMPORT_URL(
                '${envVars.apiBaseUrl}',
                '${envVars.accessToken}',
                url,
                '${connection.name}',
                '${tablename}',
                '{"autoguessing": ${preparedParams.autoguessing}}'
            );`
      case Provider.Snowflake:
        return dedent`
            DECLARE
                url STRING;
            BEGIN
                url := (SELECT ${urlColumn} FROM ${urlTable} LIMIT 1);
                CALL ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.IMPORT_URL(
                :url,
                '${connection.name}',
                '${tablename}',
                '{"autoguessing": ${preparedParams.autoguessing}}',
                '${envVars.apiBaseUrl}',
                '${envVars.accessToken}'
              );
            END;`
      case Provider.Redshift:
        return dedent`
          DECLARE
            url VARCHAR(MAX);
          BEGIN
            url := (SELECT ${urlColumn} FROM ${urlTable} LIMIT 1);
            CALL ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.IMPORT_URL(
              url,
              '${connection.name}',
              '${tablename}',
              '{"autoguessing": ${preparedParams.autoguessing}}',
              '${envVars.apiBaseUrl}',
              '${envVars.accessToken}'
            );
          END;`
      default:
        return ''
    }
  }

  getRequiredGrants({
    workflow,
    connection,
    params,
    componentName
  }: WorkflowComponentGetRequiredGrantsParams): GrantsAndAllowedApis {
    const tablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      WorkflowCodeType.Full
    )
    return {
      grants: [
        {
          connection_name: connection.name,
          source: tablename
        }
      ],
      apis: [TokenAllowedApis.imports]
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.BigQuery:
        return '1.14.0'
      case Provider.Snowflake:
        return '1.12.0'
      case Provider.Redshift:
        return '1.7.0'
      default:
        return null
    }
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.Redshift]
  }
}

export { ImportURLFromTable }
